import React from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

import ExerciseCatalog from "./ExerciseCatalog";
import ExerciseDetailsModal from "./ExerciseDetailsModal";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function BlocDetails2({ blocKey: blocKeyProp, onBack }) {
  const { t, i18n } = useTranslation("Blocs2");
  const navigate = useNavigate();
  const { blocKey: blocKeyFromRoute } = useParams();

  const blocKey = blocKeyProp || blocKeyFromRoute;

  const token = localStorage.getItem("token");

  const authHeaders = {
    Authorization: "Bearer " + token,
    "Accept-Language": i18n.language,
  };

  const jsonHeaders = {
    ...authHeaders,
    "Content-Type": "application/json",
  };

  const [form] = Form.useForm();

  const [bloc, setBloc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [fetching, setFetching] = React.useState(false);

  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedExercise, setSelectedExercise] = React.useState(null);

  const dragType = "application/x-exercise";
  const [isDragOver, setIsDragOver] = React.useState(false);

  const [ordered, setOrdered] = React.useState([]);

  const openModal = (text, isError = false) => {
    Modal[isError ? "error" : "success"]({
      title: isError
        ? t("label_error") || "Erreur"
        : t("label_success") || "Succès",
      content: text,
    });
  };

  function reconcileOrdered(serverExercises) {
    const server = Array.isArray(serverExercises) ? serverExercises : [];
    const byId = new Map(server.map((exercise) => [exercise.id, exercise]));

    const kept = ordered
      .filter((exercise) => byId.has(exercise.id))
      .map((exercise) => byId.get(exercise.id));

    const newOnes = server.filter(
      (exercise) =>
        !ordered.some((orderedExercise) => orderedExercise.id === exercise.id)
    );

    return [...kept, ...newOnes];
  }

  const fetchBloc = async () => {
    if (!blocKey) return;

    setFetching(true);

    try {
      const { data } = await axios.get(
        `${Constants.SERVER_URL}/bloc/${encodeURIComponent(blocKey)}`,
        {
          headers: authHeaders,
        }
      );

      setBloc(data);

      form.setFieldsValue({
        name: data?.name || "",
        description: data?.description || "",
      });

      setOrdered(reconcileOrdered(data?.exercises));
    } catch (err) {
      const raw =
        err?.response?.data?.message || err?.message || "unexpected_error";

      openModal(t(`Backend:${raw}`) || raw, true);
    } finally {
      setFetching(false);
    }
  };

  React.useEffect(() => {
    if (!blocKey) {
      Modal.error({
        title: t("label_error") || "Erreur",
        content: "blocKey manquant.",
        onOk: () => {
          if (onBack) {
            onBack();
          } else {
            navigate("/blocs2", { replace: true });
          }
        },
      });

      setLoading(false);
      return;
    }

    async function init() {
      setLoading(true);
      await fetchBloc();
      setLoading(false);
    }

    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocKey]);

  const onSaveBlocInfo = () => {
    const values = form.getFieldsValue();
    const name = String(values.name || "").trim();

    if (!name) {
      message.warning(
        t("label_bloc_name_required") || "Le nom du bloc est requis"
      );
      return;
    }

    axios
      .put(
        `${Constants.SERVER_URL}/update-bloc/${encodeURIComponent(blocKey)}`,
        {
          name,
          description: String(values.description || "").trim(),
        },
        {
          headers: jsonHeaders,
        }
      )
      .then((res) => {
        const raw = res?.data?.message || "bloc_updated";
          Modal.destroyAll();


        message.success(t(`Backend:${raw}`) || "Bloc mis à jour");

        // Redirection directe vers la liste des blocs
        if (onBack) {
          onBack();
        } else {
          navigate("/blocs2", { replace: true });
        }
      })
      .catch((err) => {
        const raw =
          err?.response?.data?.message || err?.message || "unexpected_error";

        openModal(
          t(`Backend:${raw}`) || "Erreur lors de la mise à jour",
          true
        );
      });
  };

  const addExercise = (payload) => {
    axios
      .post(
        `${Constants.SERVER_URL}/${encodeURIComponent(blocKey)}/add-exercise`,
        payload,
        {
          headers: jsonHeaders,
        }
      )
      .then((res) => {
        const raw = res?.data?.message || "exercise_added_to_bloc";

        message.success(t(`Backend:${raw}`) || "Exercice ajouté");

        if (payload?.exerciseId) {
          setOrdered((prev) => {
            if (prev.some((exercise) => exercise.id === payload.exerciseId)) {
              return prev;
            }

            return [
              ...prev,
              {
                id: payload.exerciseId,
                name: payload.exerciseName,
                numberOfRepetitions: payload.numberOfRepetition,
              },
            ];
          });
        }

        fetchBloc();
        setDetailsOpen(false);
      })
      .catch((err) => {
        const raw =
          err?.response?.data?.message || err?.message || "unexpected_error";

        openModal(
          t(`Backend:${raw}`) || "Impossible d’ajouter l’exercice",
          true
        );
      });
  };

  const removeExercise = (exerciseId) => {
    if (!bloc?.id) {
      message.error(t("Backend:bloc_not_found") || "Bloc introuvable");
      return;
    }

    setOrdered((prev) =>
      prev.filter((exercise) => exercise.id !== exerciseId)
    );

    axios
      .delete(`${Constants.SERVER_URL}/remove-exercise-from-bloc`, {
        headers: jsonHeaders,
        data: {
          blocId: bloc.id,
          exerciseId,
        },
      })
      .then((res) => {
        const raw = res?.data?.message || "exercise_removed_from_bloc";

        message.success(t(`Backend:${raw}`) || "Exercice retiré");
        fetchBloc();
      })
      .catch((err) => {
        const raw =
          err?.response?.data?.message || err?.message || "unexpected_error";

        openModal(t(`Backend:${raw}`) || "Suppression impossible", true);
        fetchBloc();
      });
  };

  const openExerciseDetails = (exercise) => {
    setSelectedExercise(exercise || null);
    setDetailsOpen(true);
  };

  const handleAddExerciseFromModal = (exercise, params) => {
    addExercise({
      exerciseId: exercise?.id,
      exerciseName: exercise?.name,
      numberOfSeries: Number(params?.series ?? 3),
      numberOfRepetition: Number(
        params?.repetitions ?? exercise?.numberOfRepetitions ?? 10
      ),
      minutes: Number(params?.minutes ?? 0),
      restingInstruction: String(params?.rest ?? 60),
      required: Boolean(params?.required ?? true),
    });
  };

  if (loading) {
    return (
      <div
        style={{
          padding: 24,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Spin tip={t("label_loading_bloc") || "Chargement du bloc…"} />
      </div>
    );
  }

  if (!bloc) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          t("Backend:bloc_not_found") ||
          "Bloc introuvable ou erreur de chargement"
        }
      />
    );
  }

  const handleDragOver = (event) => {
    const ok = event.dataTransfer?.types?.includes(dragType);

    if (!ok) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";

    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    const raw = event.dataTransfer.getData(dragType);

    setIsDragOver(false);

    if (!raw) return;

    event.preventDefault();

    let payload = null;

    try {
      payload = JSON.parse(raw);
    } catch {
      payload = null;
    }

    if (!payload) return;

    const exercise = {
      id: payload.id ?? null,
      key: payload.key ?? null,
      name: payload.name ?? "",
      numberOfRepetitions: payload.numberOfRepetitions ?? 10,
    };

    handleAddExerciseFromModal(exercise, {
      series: 3,
      repetitions: payload.numberOfRepetitions ?? 10,
      minutes: 0,
      rest: 60,
      required: true,
    });
  };

  return (
    <div className="bloc-details2" style={{ padding: 24 }}>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack || (() => navigate("/blocs2", { replace: true }))}
        >
          {t("label_back") || "Retour"}
        </Button>

        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("label_edit_bloc") || "Édition du bloc"}
        </Typography.Title>

        {fetching && <Tag color="blue">MAJ…</Tag>}
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title={t("label_bloc_info") || "Informations du bloc"}
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSaveBlocInfo}
              >
                {t("label_save") || "Enregistrer"}
              </Button>
            }
          >
            <Form layout="vertical" form={form}>
              <Form.Item
                label={t("label_bloc_name") || "Nom"}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("label_bloc_name_required") || "Nom requis",
                  },
                ]}
              >
                <Input
                  maxLength={80}
                  placeholder={
                    t("label_bloc_name_placeholder") || "Nom du bloc"
                  }
                />
              </Form.Item>

              <Form.Item
                label={t("label_bloc_description") || "Description"}
                name="description"
              >
                <Input.TextArea
                  placeholder={
                    t("label_bloc_description_placeholder") ||
                    "Description (optionnel)"
                  }
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  maxLength={500}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card
            style={{ marginTop: 16 }}
            title={
              <Space>
                {t("label_bloc_exercises") || "Exercices du bloc"}
                <Tag>{ordered.length}</Tag>
              </Space>
            }
            extra={
              <Space>
                <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                <Typography.Text type="secondary">
                  {t("hint_click_right_to_add") ||
                    "Clique un exercice dans la colonne de droite pour l’ajouter"}
                </Typography.Text>
              </Space>
            }
          >
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;

                const next = Array.from(ordered);
                const [moved] = next.splice(result.source.index, 1);

                next.splice(result.destination.index, 0, moved);
                setOrdered(next);
              }}
            >
              <Droppable droppableId="blocExercises" direction="horizontal">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      padding: 8,
                      border: `2px dashed ${
                        isDragOver || ordered.length === 0
                          ? "#1677ff"
                          : "transparent"
                      }`,
                      borderRadius: 12,
                      transition: "border-color .15s ease",
                    }}
                    aria-label="Déposez ici pour ajouter au bloc"
                  >
                    {ordered.length === 0 ? (
                      <Empty
                        description={
                          t("label_no_exercises") ||
                          "Aucun exercice pour l’instant"
                        }
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        {ordered.map((exercise, index) => {
                          const meta =
                            exercise?.Exercise_Bloc || exercise?.pivot || {};

                          return (
                            <Draggable
                              key={exercise.id}
                              draggableId={String(exercise.id)}
                              index={index}
                            >
                              {(dragProvided) => (
                                <Card
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  size="small"
                                  hoverable
                                  actions={[
                                    <Popconfirm
                                      key="remove"
                                      title={
                                        t("label_confirm_remove_exercise") ||
                                        "Retirer cet exercice du bloc ?"
                                      }
                                      okText={t("label_remove") || "Retirer"}
                                      cancelText={
                                        t("label_cancel") || "Annuler"
                                      }
                                      okButtonProps={{ danger: true }}
                                      onConfirm={() =>
                                        removeExercise(exercise.id)
                                      }
                                    >
                                      <DeleteOutlined
                                        style={{ color: "#ff4d4f" }}
                                      />
                                    </Popconfirm>,
                                  ]}
                                >
                                  <Space
                                    direction="vertical"
                                    style={{ width: "100%" }}
                                  >
                                    <Typography.Text strong ellipsis>
                                      {exercise.name}
                                    </Typography.Text>

                                    <Space wrap size={[8, 4]}>
                                      {meta?.numberOfSeries != null && (
                                        <Tag>
                                          📦 {meta.numberOfSeries}{" "}
                                          {t("label_series") || "séries"}
                                        </Tag>
                                      )}

                                      {meta?.numberOfRepetition != null && (
                                        <Tag>
                                          🔁 {meta.numberOfRepetition}{" "}
                                          {t("label_reps") || "reps"}
                                        </Tag>
                                      )}

                                      {meta?.minutes != null &&
                                        meta.minutes > 0 && (
                                          <Tag>
                                            ⏱ {meta.minutes}{" "}
                                            {t("label_minutes") || "min"}
                                          </Tag>
                                        )}

                                      {meta?.restingInstruction && (
                                        <Tag>🛑 {meta.restingInstruction}</Tag>
                                      )}

                                      {meta?.required && (
                                        <Tag color="red">
                                          {t("label_required") ||
                                            "Obligatoire"}
                                        </Tag>
                                      )}
                                    </Space>
                                  </Space>
                                </Card>
                              )}
                            </Draggable>
                          );
                        })}

                        {provided.placeholder}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={t("label_exercise_catalog") || "Catalogue d’exercices"}
            extra={<Tag color="purple">Nouveau flux</Tag>}
          >
            <ExerciseCatalog
              onOpenDetails={(exercise) => openExerciseDetails(exercise)}
              onAddQuick={(exercise) =>
                handleAddExerciseFromModal(exercise, {
                  series: 3,
                  repetitions: exercise?.numberOfRepetitions ?? 10,
                  minutes: 0,
                  rest: 60,
                  required: true,
                })
              }
            />
          </Card>
        </Col>
      </Row>

      <ExerciseDetailsModal
        open={detailsOpen}
        exercise={selectedExercise}
        exerciseKey={selectedExercise?.key}
        onClose={() => setDetailsOpen(false)}
        onAdd={(exercise, params) =>
          handleAddExerciseFromModal(exercise, params)
        }
      />
    </div>
  );
}